import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ExcryptionService {
private readonly GCM_IV_LENGTH = 12; // 12 bytes = 96 bits
  private readonly GCM_TAG_LENGTH = 16; // 16 bytes = 128 bits

  // Encrypt function
  async encrypt(plaintext: string, base64Key: string): Promise<string> {
    const keyBuffer = this.base64ToArrayBuffer(base64Key);
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      'AES-GCM',
      false,
      ['encrypt']
    );

    const iv = crypto.getRandomValues(new Uint8Array(this.GCM_IV_LENGTH));
    const encodedPlaintext = new TextEncoder().encode(plaintext);

    const ciphertextBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      cryptoKey,
      encodedPlaintext
    );

    // Combine IV + ciphertext
    const combined = new Uint8Array(iv.length + ciphertextBuffer.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(ciphertextBuffer), iv.length);

    // Base64 URL-safe encode (no padding, '+' => '-', '/' => '_')
    return this.arrayBufferToBase64UrlSafe(combined.buffer);
  }

  // Decrypt function
  async decrypt(base64UrlEncrypted: string, base64Key: string): Promise<string> {
    const keyBuffer = this.base64ToArrayBuffer(base64Key);
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      'AES-GCM',
      false,
      ['decrypt']
    );

    const encryptedBuffer = this.base64UrlSafeToArrayBuffer(base64UrlEncrypted);
    const iv = encryptedBuffer.slice(0, this.GCM_IV_LENGTH);
    const ciphertext = encryptedBuffer.slice(this.GCM_IV_LENGTH);

    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(iv)
      },
      cryptoKey,
      ciphertext
    );

    return new TextDecoder().decode(decryptedBuffer);
  }

  // === Helpers ===

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private arrayBufferToBase64UrlSafe(buffer: ArrayBuffer): string {
    const binary = String.fromCharCode(...new Uint8Array(buffer));
    const base64 = btoa(binary);
    // Convert to URL-safe Base64 (like Java's Base64.getUrlEncoder().withoutPadding())
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private base64UrlSafeToArrayBuffer(base64url: string): ArrayBuffer {
    // Pad to make length divisible by 4
    let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }
    return this.base64ToArrayBuffer(base64);
  }
}
